'use strict';

const libPictProvider = require('pict-provider');
const libObjectEditor = require('pict-section-objecteditor');

// The Headlight dynamic-input themes (registered by HeadlightDynamicInputsBundle): the SAME manifest renders
// read-only or editable by swapping the section views' template prefix — no separate view/edit manifests. VIEW mode
// uses the Print theme (static text, no input boxes) so the read-only metadata blends into the record's other
// label/value groups instead of showing locked input boxes.
const THEME_VIEW = 'PictProvider-Theme-Print';
const THEME_EDIT = 'PictProvider-Theme-Edit';

const BODY_SELECTOR = '#EMF-Body';

/**
 * EntityMetadataForm — a REUSABLE provider that renders an entity's per-customer "custom metadata" (its
 * CustomProperties JSON blob) as an embedded pict-section-form, whose SHAPE is a ManifestSchema (Type
 * 'EntityMetadata', SubType = the entity name), composed client-side from its partials. Shows read-only in view
 * mode / editable in edit mode (one manifest, theme-prefix swap), with an entitlement-gated "Advanced" toggle that
 * swaps the guided form for the raw-JSON object editor over the SAME bound object (so keys the manifest doesn't
 * describe are preserved, and the host's single serialize point persists everything).
 *
 * Public API: openInto({ entityType, idCustomer, dataAddress, host, mode }).
 *   - entityType : e.g. 'Project' (→ ManifestSchema SubType)
 *   - idCustomer : the record's customer (prefer that customer's manifest, else the global/first)
 *   - dataAddress: the AppData path of the live CustomProperties object the form binds to (and the host serializes)
 *   - host       : CSS selector of the mount the caller painted (e.g. '#PW-CustomPropsHost')
 *   - mode       : 'view' | 'edit'
 *
 * See [[config-app-entity-metadata]].
 */
class EntityMetadataFormProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._manifestCache = {};   // key `${entity}:${customer}` → { Loading } | { Manifest, IDManifestSchema } | { Empty } | { Error }
		this._current = null;       // the active open() descriptor
		this._advanced = false;
		this._advancedKey = '';
		this._formKey = '';
		this._ws = null;            // workspace inline-editor state (openWorkspaceMetadata), or null for the embed (openInto) path
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get formHost() { return this.pict.providers.EntityMetaFormHost; }
	get packaging() { return this.pict.services.ManifestPackagingService; }
	get app() { return this.pict.PictApplication; }
	get modal() { return this.pict.views['Pict-Section-Modal']; }
	_toast(pMessage, pType) { if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'success' }); } }

	/** True when the session may edit this entity's records (fail-open until the capability map loads). */
	_canEdit(pEntity)
	{
		const tmpSvc = this.pict.fable.EffectivePermissionsService || (this.fable && this.fable.EffectivePermissionsService);
		if (!tmpSvc || (typeof tmpSvc.can !== 'function')) { return true; }
		return !!tmpSvc.can(pEntity, 'Update');
	}

	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }

	/** True when the session may use the Advanced (raw JSON) view — fail-open until the capability map loads. */
	_canAdvanced()
	{
		const tmpSvc = this.pict.fable.EffectivePermissionsService || (this.fable && this.fable.EffectivePermissionsService);
		if (!tmpSvc || (typeof tmpSvc.can !== 'function')) { return false; }
		return !!tmpSvc.can('CustomMetadata', 'Advanced');
	}

	// ── Public entry ─────────────────────────────────────────────────────────────
	async openInto(pOptions)
	{
		const tmpOpt = pOptions || {};
		const tmpCur =
		{
			entityType: tmpOpt.entityType, idCustomer: Number(tmpOpt.idCustomer) || 0,
			dataAddress: tmpOpt.dataAddress, host: tmpOpt.host, mode: (tmpOpt.mode === 'edit') ? 'edit' : 'view',
		};
		if (!tmpCur.entityType || !tmpCur.dataAddress || !tmpCur.host) { return; }
		this._ws = null;            // embed path (caller owns the record/save) — no inline edit controls
		this._current = tmpCur;
		// Reset the advanced toggle only when the record/host changes (so a repaint keeps the user's toggle).
		const tmpAdvKey = `${tmpCur.entityType}:${tmpCur.idCustomer}:${tmpCur.dataAddress}:${tmpCur.host}`;
		if (this._advancedKey !== tmpAdvKey) { this._advanced = false; this._advancedKey = tmpAdvKey; }
		this._renderPanel();                                        // paint shell (may show Loading)
		await this._ensureManifestAsync(tmpCur.entityType, tmpCur.idCustomer);
		if (this._current === tmpCur) { this._renderPanel(); }      // repaint with the resolved manifest
	}

	// ── Public entry: self-contained inline metadata editor for a workspace tab ───────────────────────────────
	// Reads an entity record's blob COLUMN (e.g. MixDesign.MixMetadata), shows the guided form read-only with an
	// Edit → Save flow that serializes back to that column and PUTs the entity. Reusable across entities/workspaces.
	async openWorkspaceMetadata(pOptions)
	{
		const tmpOpt = pOptions || {};
		const tmpEntity = tmpOpt.entityType;
		const tmpHost = tmpOpt.host;
		const tmpRecord = tmpOpt.record;
		if (!tmpEntity || !tmpHost || !tmpRecord) { return; }
		const tmpBlobColumn = tmpOpt.blobColumn || 'CustomProperties';
		const tmpIDField = tmpOpt.idField || `ID${tmpEntity}`;
		// Already showing this exact record (e.g. a tab repaint recreated the host) — just re-attach into the new
		// host and re-render; do NOT re-parse the blob, so an in-progress edit is preserved.
		if (this._ws && (this._ws.entityType === tmpEntity) && (Number(this._ws.id) === (Number(tmpRecord[tmpIDField]) || 0)) && this._current)
		{
			this._current.host = tmpHost;
			this._renderPanel();
			return;
		}
		// The entity's Meadow field may hand us the blob as an already-parsed object (JSON-typed field) or as a raw
		// JSON string (plain text field). We write back in whichever shape we read, so Meadow stores it correctly —
		// an explicit MetadataBlob.encoding ('object' | 'string') wins when the record's blob is empty (first save).
		const tmpRaw = tmpRecord[tmpBlobColumn];
		this._ws =
		{
			entityType: tmpEntity, blobColumn: tmpBlobColumn, idField: tmpIDField, id: Number(tmpRecord[tmpIDField]) || 0,
			record: tmpRecord, canEdit: this._canEdit(tmpEntity), editing: false, saving: false, error: '',
			encoding: tmpOpt.encoding || '', blobIsObject: (tmpRaw != null) && (typeof tmpRaw === 'object'),
		};
		this.pict.AppData.EntityMetaWS = this._parseBlob(tmpRaw);
		this._advanced = false; this._advancedKey = ''; this._formKey = '';
		this._current = { entityType: tmpEntity, idCustomer: Number(tmpOpt.idCustomer) || Number(tmpRecord.IDCustomer) || 0, dataAddress: 'AppData.EntityMetaWS', host: tmpHost, mode: 'view' };
		this._renderPanel();
		await this._ensureManifestAsync(this._current.entityType, this._current.idCustomer);
		if (this._current && (this._current.host === tmpHost)) { this._renderPanel(); }
	}

	_parseBlob(pRaw)
	{
		// Always hand back an INDEPENDENT working copy. For an already-parsed object (JSON-typed Meadow field) a deep
		// clone is essential — otherwise the form would marshal straight into the record's own object, clobbering the
		// original in-memory and leaving Cancel nothing to revert to.
		if (pRaw && (typeof pRaw === 'object')) { try { return JSON.parse(JSON.stringify(pRaw)); } catch (pE) { return {}; } }
		try { const tmpO = JSON.parse(pRaw || '{}'); return (tmpO && (typeof tmpO === 'object')) ? tmpO : {}; } catch (pE) { return {}; }
	}

	/** Shape the metadata object for the entity's blob field — mirror the read shape (or the config's encoding). */
	_serializeBlob(pObj)
	{
		const tmpObj = pObj || {};
		const tmpWantsObject = (this._ws && this._ws.encoding) ? (this._ws.encoding === 'object') : !!(this._ws && this._ws.blobIsObject);
		return tmpWantsObject ? tmpObj : JSON.stringify(tmpObj);
	}

	/** Enter inline edit mode (workspace tab). */
	wsEdit()
	{
		if (!this._ws || !this._ws.canEdit || this._ws.saving) { return; }
		this._ws.editing = true; this._ws.error = ''; this._advanced = false; this._formKey = '';
		this._current.mode = 'edit';
		this._renderPanel();
	}

	/** Discard edits + return to read-only. */
	wsCancel()
	{
		if (!this._ws || this._ws.saving) { return; }
		this.pict.AppData.EntityMetaWS = this._parseBlob(this._ws.record[this._ws.blobColumn]);   // revert to the loaded blob
		this._ws.editing = false; this._ws.error = ''; this._advanced = false; this._formKey = '';
		this._current.mode = 'view';
		this._renderPanel();
	}

	/** Serialize the metadata back to the entity's blob column and PUT the entity. */
	async wsSave()
	{
		if (!this._ws || !this._ws.editing || this._ws.saving) { return; }
		this.flush();   // marshal form → AppData.EntityMetaWS + restore the manifest's declared types
		this._ws.saving = true; this._ws.error = '';
		this._renderPanel();
		const tmpObj = this.pict.AppData.EntityMetaWS || {};
		const tmpSerialized = this._serializeBlob(tmpObj);
		const tmpBody = {};
		tmpBody[this._ws.idField] = this._ws.id;
		tmpBody[this._ws.blobColumn] = tmpSerialized;
		const tmpRes = await new Promise((resolve) => this.restClient.putJSON(this._ws.entityType, tmpBody, (pErr, pBody) => resolve({ error: pErr, result: pBody })));
		this._ws.saving = false;
		if (tmpRes.error || (tmpRes.result && tmpRes.result.ErrorCode))
		{
			this._ws.error = 'Could not save the metadata (you may not have permission).';
			this._renderPanel();
			this._toast(this._ws.error, 'error');
			return;
		}
		this._ws.record[this._ws.blobColumn] = tmpSerialized;   // keep the in-memory record in sync (same shape we sent)
		this._ws.editing = false; this._formKey = '';
		this._current.mode = 'view';
		this._renderPanel();
		this._toast('Metadata saved.', 'success');
	}

	// ── Manifest load + compose (client-side; the service's pure merge helper works in-browser) ──────────────
	async _ensureManifestAsync(pEntity, pCustomer)
	{
		const tmpKey = `${pEntity}:${pCustomer}`;
		const tmpExisting = this._manifestCache[tmpKey];
		if (tmpExisting && !tmpExisting.Loading) { return tmpExisting; }
		if (tmpExisting && tmpExisting.Loading) { return tmpExisting; }
		this._manifestCache[tmpKey] = { Loading: true };
		try
		{
			const tmpRows = await this._getJSON(`ManifestSchemas/FilteredTo/FBV~Type~EQ~EntityMetadata~FBV~SubType~EQ~${encodeURIComponent(pEntity)}~FBV~Deleted~EQ~0~FSF~Version~DESC~0/0/50`);
			const tmpList = Array.isArray(tmpRows) ? tmpRows : [];
			// Prefer this customer's manifest; else a Global (IDCustomer 0) default; else the newest row.
			const tmpRow = tmpList.find((pR) => Number(pR.IDCustomer) === pCustomer)
				|| tmpList.find((pR) => Number(pR.IDCustomer) === 0) || tmpList[0] || null;
			if (!tmpRow) { this._manifestCache[tmpKey] = { Empty: true }; return this._manifestCache[tmpKey]; }
			const tmpRoot = (typeof tmpRow.Manifest === 'string') ? JSON.parse(tmpRow.Manifest || '{}') : (tmpRow.Manifest || {});
			// Fetch + order the partials (Ordinal lives on the join row).
			let tmpPartials = [];
			const tmpJoins = await this._getJSON(`ManifestSchemaManifestSchemaPartialJoins/FilteredTo/FBV~IDManifestSchema~EQ~${tmpRow.IDManifestSchema}`);
			const tmpJoinList = Array.isArray(tmpJoins) ? tmpJoins : [];
			if (tmpJoinList.length)
			{
				const tmpOrd = {};
				tmpJoinList.forEach((pJ) => { tmpOrd[Number(pJ.IDManifestSchemaPartial)] = Number(pJ.Ordinal) || 0; });
				const tmpIDs = tmpJoinList.map((pJ) => Number(pJ.IDManifestSchemaPartial)).filter(Boolean);
				const tmpPRows = await this._getJSON(`ManifestSchemaPartials/FilteredTo/FBL~IDManifestSchemaPartial~INN~${tmpIDs.join(',')}~FBV~Deleted~EQ~0/0/500`);
				tmpPartials = (Array.isArray(tmpPRows) ? tmpPRows : [])
					.map((pP) => ({ ManifestPartial: (typeof pP.ManifestPartial === 'string') ? JSON.parse(pP.ManifestPartial || '{}') : (pP.ManifestPartial || {}), __id: Number(pP.IDManifestSchemaPartial) }))
					.sort((pA, pB) => (tmpOrd[pA.__id] || 0) - (tmpOrd[pB.__id] || 0));
			}
			// Compose. _mergeManifests MUTATES its root in place, so clone first.
			let tmpManifest = tmpRoot;
			if (this.packaging && (typeof this.packaging._mergeManifests === 'function'))
			{
				const tmpPacked = this.packaging._mergeManifests({ Manifest: JSON.parse(JSON.stringify(tmpRoot)) }, tmpPartials);
				tmpManifest = (tmpPacked && tmpPacked.DefaultFormManifest) || tmpRoot;
			}
			this._manifestCache[tmpKey] = { Manifest: tmpManifest, IDManifestSchema: tmpRow.IDManifestSchema };
		}
		catch (pError)
		{
			this._manifestCache[tmpKey] = { Error: (pError && pError.message) ? pError.message : String(pError) };
		}
		return this._manifestCache[tmpKey];
	}

	// ── Render ───────────────────────────────────────────────────────────────────
	_renderPanel()
	{
		this._ensureStyles();
		const tmpCur = this._current;
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCur || !tmpCA.getElement(tmpCur.host).length) { return; }
		const tmpEntry = this._manifestCache[`${tmpCur.entityType}:${tmpCur.idCustomer}`] || {};
		const tmpCanAdv = this._canAdvanced();
		const tmpToggle = tmpCanAdv
			? `<button type="button" class="emf-toggle" onclick="_Pict.providers.EntityMetadataForm.toggleAdvanced()">${this._advanced ? '<i class="fas fa-list-check"></i> Guided view' : '<i class="fas fa-code"></i> Advanced (JSON)'}</button>`
			: '';
		// Workspace inline-edit controls (openWorkspaceMetadata path only; the embed path's caller owns edit/save).
		let tmpWsBtns = '';
		if (this._ws)
		{
			if (this._ws.editing)
			{
				tmpWsBtns = `<button type="button" class="emf-btn emf-btn-primary"${this._ws.saving ? ' disabled' : ''} onclick="_Pict.providers.EntityMetadataForm.wsSave()">${this._ws.saving ? 'Saving…' : 'Save'}</button>`
					+ `<button type="button" class="emf-btn"${this._ws.saving ? ' disabled' : ''} onclick="_Pict.providers.EntityMetadataForm.wsCancel()">Cancel</button>`;
			}
			else if (this._ws.canEdit)
			{
				tmpWsBtns = `<button type="button" class="emf-btn" onclick="_Pict.providers.EntityMetadataForm.wsEdit()"><i class="fas fa-pen"></i> Edit</button>`;
			}
		}
		const tmpErr = (this._ws && this._ws.error) ? `<div class="emf-note emf-error">${this._esc(this._ws.error)}</div>` : '';
		// No title — the caller's panel already heads this section; we add only the edit controls + advanced toggle.
		const tmpHead = (tmpWsBtns || tmpToggle) ? `<div class="emf-head">${tmpWsBtns}${tmpToggle}</div>` : '';
		tmpCA.assignContent(tmpCur.host, `<div class="emf-panel">${tmpHead}${tmpErr}<div id="EMF-Body" class="emf-body"></div></div>`);
		// Neutralize the host's leftover object-editor box (border / grey bg / fixed-height overflow:auto) so the
		// form just flows and grows vertically like the record's other groups — no scroll box.
		const tmpHostEl = tmpCA.getElement(tmpCur.host);
		if (tmpHostEl && tmpHostEl[0] && tmpHostEl[0].style)
		{
			const tmpEl = tmpHostEl[0];
			tmpEl.style.overflow = 'visible'; tmpEl.style.maxHeight = 'none'; tmpEl.style.minHeight = '0';
			tmpEl.style.border = 'none'; tmpEl.style.background = 'transparent'; tmpEl.style.padding = '0';
		}
		if (tmpEntry.Loading) { tmpCA.assignContent(BODY_SELECTOR, '<div class="emf-note"><i class="fas fa-spinner fa-spin"></i> Loading…</div>'); return; }
		// Advanced (entitled) → raw object editor. Also the fallback when there is no manifest for this entity/customer.
		if ((this._advanced && tmpCanAdv) || tmpEntry.Empty || tmpEntry.Error || !tmpEntry.Manifest)
		{
			if (tmpEntry.Error) { /* fall through to the raw editor so the data is still shown + editable */ }
			this._mountRaw();
			return;
		}
		this._mountForm(tmpEntry.Manifest);
	}

	_mountForm(pManifest)
	{
		const tmpCur = this._current;
		const tmpHost = this.formHost;
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpHost || (typeof tmpHost.loadForm !== 'function')) { tmpCA.assignContent(BODY_SELECTOR, '<div class="emf-note">The form runner is not available.</div>'); return; }
		const tmpKey = `${tmpCur.entityType}:${tmpCur.idCustomer}:${tmpCur.dataAddress}:${tmpCur.mode}`;
		try
		{
			if ((this._formKey === tmpKey) && (typeof tmpHost.isLoaded === 'function') && tmpHost.isLoaded())
			{
				// Same form; the host div was just recreated by a repaint — re-render into it + keep the theme.
				tmpHost.options.DestinationAddress = BODY_SELECTOR;
				this._applyTheme(tmpHost, tmpCur.mode);
				tmpHost.render();
			}
			else
			{
				tmpHost.loadForm(pManifest, { DestinationAddress: BODY_SELECTOR, DataAddress: tmpCur.dataAddress });
				this._formKey = tmpKey;
				this._applyTheme(tmpHost, tmpCur.mode);
				tmpHost.render();
				// Section views can be built a tick after loadForm (async inputs); re-apply the theme once more.
				setTimeout(() => { try { if (this._formKey === tmpKey) { this._applyTheme(tmpHost, tmpCur.mode); tmpHost.render(); } } catch (pE) { /* */ } }, 0);
			}
		}
		catch (pError) { tmpCA.assignContent(BODY_SELECTOR, `<div class="emf-note emf-error">Could not render the form: ${this._esc((pError && pError.message) || pError)}</div>`); }
	}

	/** Swap the section views' template prefix so one manifest renders read-only (view) or editable (edit). */
	_applyTheme(pHost, pMode)
	{
		const tmpPrefix = (pMode === 'edit') ? THEME_EDIT : THEME_VIEW;
		try
		{
			const tmpViews = (typeof pHost.getSectionViews === 'function') ? (pHost.getSectionViews() || []) : [];
			tmpViews.forEach((pView) => { if (pView) { pView.customDefaultTemplatePrefix = tmpPrefix; } });
		}
		catch (pError) { /* best-effort; falls back to the global theme */ }
	}

	// ── Raw JSON (object editor) — advanced, and the no-manifest fallback ─────────────────────────────────────
	_mountRaw()
	{
		const tmpCur = this._current;
		const tmpView = this._ensureRawView(tmpCur.dataAddress, tmpCur.mode === 'edit');
		if (!tmpView) { this.pict.ContentAssignment.assignContent(BODY_SELECTOR, '<div class="emf-note">The raw editor is not available.</div>'); return; }
		tmpView.options.DefaultDestinationAddress = BODY_SELECTOR;
		try { tmpView.render(); } catch (pError) { this.pict.ContentAssignment.assignContent(BODY_SELECTOR, `<div class="emf-note emf-error">Could not render the raw editor: ${this._esc((pError && pError.message) || pError)}</div>`); }
	}

	_ensureRawView(pDataAddress, pEditable)
	{
		const tmpHash = `EMF-Raw-${String(pDataAddress).replace(/[^A-Za-z0-9]/g, '_')}-${pEditable ? 'edit' : 'view'}`;
		if (this.pict.views[tmpHash]) { return this.pict.views[tmpHash]; }
		this.pict.addView(tmpHash, Object.assign({}, libObjectEditor.default_configuration,
			{
				ViewIdentifier: tmpHash, AutoRender: false, Editable: !!pEditable, InitialExpandDepth: 2,
				ObjectDataAddress: pDataAddress,
				Renderables: [ { RenderableHash: 'ObjectEditor-Container', TemplateHash: 'ObjectEditor-Container-Template', DestinationAddress: BODY_SELECTOR, RenderMethod: 'replace' } ],
			}), libObjectEditor);
		const tmpView = this.pict.views[tmpHash];
		if (tmpView && (typeof tmpView.initialize === 'function')) { tmpView.initialize(); }
		return tmpView;
	}

	/**
	 * Flush any pending guided-form edit into the bound object — call before the host serializes CustomProperties, so
	 * a value typed but not yet blurred is captured. No-op in advanced mode (the object editor mutates in place) or
	 * when no form is loaded. The form already auto-marshals on change; this just closes the unblurred-field gap.
	 */
	flush()
	{
		const tmpHost = this.formHost;
		if (!this._advanced && tmpHost && (typeof tmpHost.save === 'function') && (typeof tmpHost.isLoaded === 'function') && tmpHost.isLoaded())
		{
			try { tmpHost.save(); } catch (pError) { /* best-effort marshal */ }
			this._coerceTypes();
		}
	}

	/** The form marshals every input as a string; restore the manifest's declared types (Number/Boolean) on the bound
	 *  object so a numeric metadata value doesn't silently become a string on save. Date/Time stay strings. */
	_coerceTypes()
	{
		const tmpCur = this._current;
		if (!tmpCur) { return; }
		const tmpEntry = this._manifestCache[`${tmpCur.entityType}:${tmpCur.idCustomer}`];
		const tmpManifest = tmpEntry && tmpEntry.Manifest;
		const tmpDescriptors = tmpManifest && tmpManifest.Descriptors;
		const tmpObj = this._resolveAddress(tmpCur.dataAddress);
		if (!tmpDescriptors || !tmpObj || (typeof tmpObj !== 'object')) { return; }
		Object.keys(tmpDescriptors).forEach((pKey) =>
		{
			const tmpD = tmpDescriptors[pKey] || {};
			const tmpAddr = tmpD.DataAddress || pKey;
			if (tmpAddr.indexOf('.') > -1) { return; }                 // only top-level metadata keys (P1)
			if (!Object.prototype.hasOwnProperty.call(tmpObj, tmpAddr)) { return; }
			const tmpVal = tmpObj[tmpAddr];
			const tmpType = tmpD.DataType;
			if ((tmpType === 'Number') || (tmpType === 'PreciseNumber'))
			{
				if ((tmpVal === '') || (tmpVal == null)) { tmpObj[tmpAddr] = null; }
				else if ((typeof tmpVal === 'string') && (tmpVal.trim() !== '') && !isNaN(Number(tmpVal))) { tmpObj[tmpAddr] = Number(tmpVal); }
			}
			else if (tmpType === 'Boolean')
			{
				if (typeof tmpVal === 'string') { tmpObj[tmpAddr] = (tmpVal === 'true') || (tmpVal === '1') || (tmpVal === 'on'); }
			}
		});
	}

	/** Walk a Pict address (e.g. 'AppData.ProjectEditWorkspace.CustomPropsData') from the pict root to the live object. */
	_resolveAddress(pAddress)
	{
		const tmpParts = String(pAddress || '').split('.');
		let tmpNode = this.pict;
		for (let i = 0; i < tmpParts.length; i++) { if (tmpNode == null) { return null; } tmpNode = tmpNode[tmpParts[i]]; }
		return tmpNode;
	}

	toggleAdvanced()
	{
		if (!this._canAdvanced()) { return; }
		this._advanced = !this._advanced;
		// Force the guided form to re-mount when toggling back (the host div is replaced by the panel repaint).
		this._formKey = '';
		this._renderPanel();
	}

	_ensureStyles()
	{
		if ((typeof document === 'undefined') || document.getElementById('emf-styles')) { return; }
		const tmpStyle = document.createElement('style');
		tmpStyle.id = 'emf-styles';
		tmpStyle.textContent = /*css*/`
			.emf-panel { margin-top: 0.4rem; }
			.emf-head { display:flex; align-items:center; justify-content:flex-end; gap:0.75rem; margin:0 0 0.4rem; }
			.emf-toggle { font-size:0.74rem; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:0.35rem; padding:0.25rem 0.6rem; border-radius:6px; border:1px solid var(--theme-color-border-medium,#d5dbe3); background:var(--theme-color-background-panel,#fff); color:var(--theme-color-text-secondary,#45505f); }
			.emf-toggle:hover { border-color:var(--theme-color-primary,#2f6fed); color:var(--theme-color-primary,#2f6fed); }
			.emf-btn { font-size:0.78rem; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:0.35rem; padding:0.28rem 0.7rem; border-radius:6px; border:1px solid var(--theme-color-border-medium,#d5dbe3); background:var(--theme-color-background-panel,#fff); color:var(--theme-color-text-primary,#1f2733); }
			.emf-btn:hover:not([disabled]) { border-color:var(--theme-color-primary,#2f6fed); color:var(--theme-color-primary,#2f6fed); }
			.emf-btn[disabled] { opacity:0.6; cursor:default; }
			.emf-btn-primary { color:#fff; background:var(--theme-color-primary,#2f6fed); border-color:var(--theme-color-primary,#2f6fed); }
			.emf-btn-primary:hover:not([disabled]) { color:#fff; opacity:0.92; }
			.emf-note { font-size:0.85rem; color:var(--theme-color-text-muted,#6b7686); padding:0.5rem 0; }
			.emf-error { color:var(--theme-color-status-danger,#c0392b); }
		`;
		try { document.head.appendChild(tmpStyle); } catch (pError) { /* */ }
	}
}

EntityMetadataFormProvider.default_configuration = { ProviderIdentifier: 'EntityMetadataForm' };

module.exports = EntityMetadataFormProvider;
module.exports.default_configuration = EntityMetadataFormProvider.default_configuration;
